/**
 * LinkedIn Post Content Extractor
  */

interface LinkedInPostData {
  content: string;
  success: boolean;
  error?: string;
}

/**
 * Expand LinkedIn shortened URLs (lnkd.in) to their actual destinations
 */
async function expandLinkedInShortenedUrls(content: string): Promise<string> {
  try {
    // Regex to find all lnkd.in URLs in the content
    const lnkdUrlRegex = /https?:\/\/lnkd\.in\/[a-zA-Z0-9_-]+/gi;
    const matches = content.match(lnkdUrlRegex);
    
    if (!matches || matches.length === 0) {
      return content; // No shortened URLs found
    }

    let expandedContent = content;
    
    // Expand each shortened URL
    for (const shortenedUrl of matches) {
      try {
        const actualUrl = await resolveShortUrlRedirect(shortenedUrl);
        if (actualUrl && actualUrl !== shortenedUrl) {
          // Replace the shortened URL with the actual URL
          expandedContent = expandedContent.replace(shortenedUrl, actualUrl);
          // console.log(`Expanded ${shortenedUrl} → ${actualUrl}`);
        }
      } catch (error) {
        // console.warn(`Failed to expand ${shortenedUrl}:`, error);
        // Continue with other URLs even if one fails
      }
    }
    
    return expandedContent;
  } catch (error) {
    // console.error("Error expanding shortened URLs:", error);
    return content; // Return original content if expansion fails
  }
}

/**
 * Resolve a shortened URL to its actual destination by following redirects
 */
async function resolveShortUrlRedirect(shortUrl: string): Promise<string> {
  try {
    // Call our backend API to resolve the redirect
    // We need to use the backend to avoid CORS issues
    const response = await fetch('/api/linkedin/resolve-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: shortUrl })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.actualUrl) {
        return data.actualUrl;
      }
    }
    
    return shortUrl; // Fallback to original if resolution fails
  } catch (error) {
    // console.warn(`Failed to resolve ${shortUrl}:`, error);
    return shortUrl;
  }
}

export async function extractLinkedInPostContent(url: string): Promise<LinkedInPostData> {
  try {
    // Validate LinkedIn URL
    if (!isValidLinkedInPostUrl(url)) {
      return {
        success: false,
        content: "",
        error: "Invalid LinkedIn post URL. Please provide a valid post URL."
      };
    }

    // Strategy 1: Use backend proxy endpoint
    try {
      const response = await fetch('/api/linkedin/extract-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.content) {
          // Expand any LinkedIn shortened URLs (lnkd.in) before returning
          const expandedContent = await expandLinkedInShortenedUrls(data.content);
          
          return {
            success: true,
            content: expandedContent
          };
        }
      }
    } catch (backendError) {
      // console.log("Backend extraction not available");
    }

    // If all strategies fail
    return {
      success: false,
      content: "",
      error: "Unable to automatically extract post content. Please try again or contact support."
    };

  } catch (error) {
    // console.error("Error extracting LinkedIn content:", error);
    return {
      success: false,
      content: "",
      error: "An error occurred while extracting post content. Please try again."
    };
  }
}

/**
 * Validate if the URL is a valid LinkedIn post URL
 */
function isValidLinkedInPostUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return (
      (urlObj.hostname === 'www.linkedin.com' || urlObj.hostname === 'linkedin.com') &&
      (urlObj.pathname.includes('/posts/') || urlObj.pathname.includes('/feed/update/'))
    );
  } catch {
    return false;
  }
}

/**
 * Alternative: Extract post ID from URL for API calls
 */
export function extractLinkedInPostId(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    
    // LinkedIn post URLs are typically: /posts/{username}_{id} or /feed/update/urn:li:activity:{id}
    if (urlObj.pathname.includes('/posts/')) {
      const postPart = pathParts[pathParts.indexOf('posts') + 1];
      return postPart || null;
    }
    
    if (urlObj.pathname.includes('/feed/update/')) {
      const updatePart = pathParts[pathParts.indexOf('update') + 1];
      return updatePart || null;
    }
    
    return null;
  } catch {
    return null;
  }
}
